import { IconButton, InputBase, Stack, styled, type Theme, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { type KeyboardEvent, type MouseEvent, useState } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import { STEP_NAME_MAX_LENGTH } from 'src/pages/Settings/workflows/utils/workflow-definition';
import { useWorkflowEditor } from 'src/pages/Settings/workflows/workflow-editor-context';

export interface StepTitleProps {
  nodeId: string;
  /** The stored name; empty when the step has none and is titled by its type. */
  name: string;
  /** The step type's label, or "Unknown step": the title without a name, and the input's hint. */
  typeLabel: string;
  /** A selected step of an editable draft: the pencil shows on hover. */
  renamable: boolean;
}

// the title and its input share one height, so starting a rename doesn't move the card
const titleHeight = (theme: Theme) => theme.spacing(3.5);

const TitleRow = styled(Stack)(({ theme }) => ({
  height: titleHeight(theme),
  flexDirection: 'row',
  alignItems: 'center',
  minWidth: 0,
  flex: 1,
  '& .workflow-node__rename': { opacity: 0 },
  '&:hover .workflow-node__rename, & .workflow-node__rename:focus-visible': { opacity: 1 },
}));

const Title = styled(Typography)(({ theme }) => ({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontWeight: 600,
  color:
    theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.text.primary,
}));

// borderless, a light tint over the header, in the title's own type
const TitleInput = styled(InputBase)(({ theme }) => ({
  ...theme.typography.subtitle2,
  fontWeight: 600,
  height: titleHeight(theme),
  flex: 1,
  minWidth: 0,
  borderRadius: theme.shape.borderRadius,
  // the text keeps clear of the tint's edge
  paddingInline: theme.spacing(0.5),
  backgroundColor: alpha(theme.palette.custom.OV.sys.primary, 0.12),
  color:
    theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.text.primary,
  '& .MuiInputBase-input': { padding: 0 },
}));

// the card opens, closes and gets selected on clicks; renaming does none of that
const stopPropagation = (event: MouseEvent) => event.stopPropagation();

/**
 * A step card's title: its name, or its type's label when it has none. When `renamable`, a pencil
 * on hover turns it into an input: Enter and clicking away save, Escape cancels without closing the
 * card. Saving an empty name gives the step its type's title back.
 */
export const StepTitle = ({ nodeId, name, typeLabel, renamable }: StepTitleProps) => {
  const { dispatch } = useWorkflowEditor();
  // what is being typed; null while the title is just shown
  const [draft, setDraft] = useState<string | null>(null);
  const title = name || typeLabel;

  const save = () => {
    if (draft === null) return;
    setDraft(null);
    if (draft.trim() !== name) dispatch({ kind: 'renameStep', nodeId, name: draft });
  };

  const onKeyDown = (event: KeyboardEvent) => {
    // keys typed here are the input's: the card's Escape and the canvas's shortcuts stay out
    event.stopPropagation();
    if (event.key === 'Enter') save();
    if (event.key === 'Escape') setDraft(null);
  };

  if (draft !== null) {
    return (
      <TitleInput
        className="nodrag nopan"
        autoFocus
        value={draft}
        placeholder={typeLabel}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={onKeyDown}
        // reopening selects the whole name, so typing replaces it
        onFocus={(event) => event.target.select()}
        onBlur={save}
        onClick={stopPropagation}
        onDoubleClick={stopPropagation}
        inputProps={{ maxLength: STEP_NAME_MAX_LENGTH, 'aria-label': 'Step name' }}
      />
    );
  }

  return (
    <TitleRow>
      <Title variant="subtitle2" title={title}>
        {title}
      </Title>
      {renamable ? (
        <Tooltip title="Rename step">
          <IconButton
            size="small"
            className="nodrag workflow-node__rename"
            aria-label="Rename step"
            onClick={(event) => {
              event.stopPropagation();
              setDraft(name);
            }}
          >
            <DynamicIcon name="edit" sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ) : null}
    </TitleRow>
  );
};
